import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type { SigningActionResponse } from "@/shared/types";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";
import type { OrderOnHiringResponse, OrderOnHiringNested, CompleteHiringResponse } from "@/features/hr/contracts/types";

export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrderOnHiring: builder.query<OrderOnHiringNested, number>({
      query: (orderId) => ({
        url: `/api/hr/orders-on-hiring/${orderId}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, orderId) => [
        { type: "Contracts" as const, id: "LIST" },
        { type: "Applications" as const, id: orderId },
      ],
    }),
    previewOrderOnHiring: builder.mutation<FileResponse, number>({
      query: (orderId) => ({
        url: `/api/hr/orders-on-hiring/${orderId}/preview/`,
        method: "GET",
        credentials: "include",
        responseHandler: fileResponseHandler,
      }),
    }),
    uploadOrderOnHiring: builder.mutation<OrderOnHiringResponse, { orderId: number; file: File }>({
      query: ({ orderId, file }) => {
        const formData = new FormData();
        formData.append("signed_pdf", file);
        return {
          url: `/api/hr/orders-on-hiring/${orderId}/upload/`,
          method: "POST",
          body: formData,
          credentials: "include",
        };
      },
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: "Contracts" as const, id: "LIST" },
        { type: "Applications" as const, id: orderId },
      ],
    }),
    signOrderOnHiring: builder.mutation<SigningActionResponse, { orderId: number; cms_signature: string }>({
      query: ({ orderId, cms_signature }) => ({
        url: `/api/hr/orders-on-hiring/${orderId}/sign/`,
        method: "POST",
        body: { cms_signature },
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: "Contracts" as const, id: "LIST" },
        { type: "Applications" as const, id: orderId },
        "PendingActions",
      ],
      async onQueryStarted({ orderId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) =>
                item.action_required === "sign_document" &&
                "document_id" in item.details &&
                (item.details as SignDocumentDetails).source_type === "hiring_order" &&
                item.id === orderId,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
    completeHiring: builder.mutation<CompleteHiringResponse, number>({
      query: (orderId) => ({
        url: `/api/hr/orders-on-hiring/${orderId}/complete/`,
        method: "POST",
        body: {},
        credentials: "include",
      }),
      invalidatesTags: () => [
        { type: "Contracts" as const, id: "LIST" },
        { type: "NGOWorkers" as const, id: "LIST" },
        { type: "Applications" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),
  }),
});

export const {
  useGetOrderOnHiringQuery,
  usePreviewOrderOnHiringMutation,
  useUploadOrderOnHiringMutation,
  useSignOrderOnHiringMutation,
  useCompleteHiringMutation,
} = ordersApi;
